import { CARRIER, PATTERN } from '../config/constants.js'
import { NM_TO_FT } from '../util/units.js'

/**
 * The CASE 1 pattern, as a sequence of legs.
 *
 * Each leg says three things: where to point the aeroplane, what to do
 * vertically, and what ends the leg. Nothing here knows about the pilot, the
 * checklist or the keyboard — that lives in src/procedures. Keeping the two
 * apart means the pattern can be flown and measured in a test with no user at
 * all, which is how the geometry below gets checked.
 *
 * GUIDANCE MODES
 *   track  — fly a straight ground track. Given as a line through a point in
 *            the ship's own frame with a heading offset from base recovery
 *            course. The controller banks to capture it, so it is self-
 *            correcting: however the previous turn came out, the next straight
 *            leg cleans it up.
 *   turn   — hold a bank angle until the heading has swept far enough. Used
 *            for the break and the approach turn, where the *radius* is the
 *            point of the manoeuvre rather than an arrival somewhere.
 *   groove — track the extended angled-deck centreline. Separate from `track`
 *            because that line is not parallel to anything on the ship: it is
 *            9 degrees to port of the hull and it slides sideways under you at
 *            ship speed, which is why you fly the groove crabbed.
 *
 * WHERE THE NUMBERS COME FROM
 * The abeam distance and the bank angle in the approach turn are not style
 * choices, they are the solution to one equation. The turn has to do three
 * things at once: sweep about 189 degrees (180 plus the angled deck), close
 * the lateral gap between the downwind line and the extended centreline, and
 * take long enough that the ship steams far enough away to leave you a groove
 * worth flying.
 *
 * That last one is the surprise. Everything the carrier travels during the
 * turn becomes groove length, and it is the biggest term by far — bigger than
 * the abeam distance, bigger than the bank angle. A tight turn is over in 59
 * seconds, the ship makes under 3,000 ft in that time, and you roll out a third
 * of a mile astern with a thirteen second groove. That is not a CASE 1.
 *
 * These numbers are SOLVED, not chosen. `scripts/sweep.mjs` flies the whole
 * parameter space — abeam distance against bank angle against where the turn
 * hands over to the lineup capture — and checks each combination against all
 * six published limits at once. 141 of 1,440 satisfy every one of them; this is
 * the pick, and the reasoning for the abeam distance in particular is in
 * config/constants.js because it is the number that had to give.
 *
 * scripts/test-rails.mjs then flies the chosen one and asserts it, so retuning
 * the flight model or the ship's speed fails the build rather than quietly
 * producing a pattern that no longer resembles a recovery.
 */

const ABEAM_FT = PATTERN.DOWNWIND_ABEAM_DISTANCE_NM * NM_TO_FT

/** See above: solved, not chosen. */
export const APPROACH_TURN_BANK_DEG = 23.5

/** Left-hand pattern: the break, the turn and the downwind are all to port. */
export const TURN_DIRECTION = -1

export const LEGS = [
  {
    id: 'INITIAL',
    label: 'THE INITIAL',
    brief: '800 feet, 350 knots, up the starboard side toward the bow.',
    guidance: { mode: 'track', originFt: [0, 220], headingOffsetDeg: 0 },
    altFt: PATTERN.INITIAL_ALT_FT,
    speedKtas: PATTERN.INITIAL_SPEED_KTS,
    flapNorm: 0,
    // The break is flown at the bow, not over the ramp. Anything earlier and
    // the downwind ends up too far forward to make the abeam position work.
    done: (nav) => nav.ship.x > CARRIER.HULL_LENGTH_FT / 2,
  },

  {
    id: 'BREAK',
    label: 'THE BREAK',
    brief: 'Hard left turn, idle and boards, bleeding to on speed.',
    guidance: { mode: 'turn', bankDeg: PATTERN.BREAK_BANK_DEG },
    // LEVEL at 800. The book is explicit about it — "level turn at 800 feet" —
    // and the descent to 600 belongs on the downwind, where the chart puts it.
    altFt: PATTERN.BREAK_ALT_FT,
    speedKtas: 190,
    // Idle and boards in a 60 degree turn is the most deceleration a Tomcat
    // has available without hurting anyone. It is also the entire purpose of
    // the break: 350 knots has to become approach speed before the abeam, and
    // there is not much pattern left to do it in.
    decelKtS: 7.0,
    flapNorm: 0,
    // 150 degrees of sweep, not 180: the downwind capture finishes the job and
    // rolling out on a heading you then have to correct looks worse than
    // rolling out slightly early and settling onto the line.
    done: (nav) => nav.turnedDeg > 150,
  },

  {
    id: 'DOWNWIND',
    label: 'DOWNWIND',
    brief: '600 feet, 1.3 miles abeam, dirty up and slow to on speed.',
    guidance: { mode: 'track', originFt: [0, -ABEAM_FT], headingOffsetDeg: 180 },
    altFt: PATTERN.DOWNWIND_ALT_FT,
    speedKtas: null, // on speed, solved from weight and bank
    // Dirty and at idle a Tomcat sheds speed fast, and it has to: the downwind
    // is barely fifteen seconds and there are sixty-odd knots to lose in it.
    // At the break's rate the jet arrives at the abeam twelve knots fast,
    // which is a real error and one nobody would ever see on rails.
    decelKtS: 6.0,
    flapNorm: 1,
    // Abeam the LSO platform. Computed from where the platform actually is
    // rather than from a round number, because that is what "abeam" means.
    done: (nav) => nav.ship.x < nav.lsoShipX,
  },

  {
    id: 'APPROACH_TURN',
    label: 'THE 180',
    brief: 'Twenty-three degrees of bank, descending. Hold on-speed AoA all the way.',
    // A steady bank until the deck is roughly ahead, then the centreline
    // capture takes over and flies the rest of the turn. That handover is not
    // a shortcut — it is what the pilot does. From about the 90 you stop
    // flying a bank angle and start flying the lineup, and a turn that holds
    // one bank angle all the way round rolls out wherever the arithmetic left
    // it.
    //
    // The handover angle and the capture lead are what decide WHERE it rolls
    // out, and where it rolls out is the groove length — aim close and the turn
    // cuts the corner and hands you a third of a mile; aim wide and it rolls
    // out long. Both were solved in the sweep along with the bank.
    guidance: {
      mode: 'turn',
      bankDeg: APPROACH_TURN_BANK_DEG,
      blendToCentrelineBelowDeg: 70,
      blendMaxBankDeg: 26,
      captureLeadFt: 5000,
    },
    altFt: null,
    // 600 at the abeam, 450-500 at the 90, and NOT on the glidepath yet — the
    // book has you level at the 90 and intercepting the glideslope three
    // quarters of a mile out. Joining it early, which is what this used to do,
    // has you descending through the 90 fifty feet low and turns the whole
    // back half of the turn into a slow drift down rather than an interception.
    altFromTurn: [PATTERN.ABEAM_ALT_FT, PATTERN.GLIDESLOPE_INTERCEPT_ALT_FT],
    captureGlidepath: true,
    speedKtas: null,
    flapNorm: 1,
    /** Rolled out: nose on the groove heading and near the centreline. */
    done: (nav) =>
      (Math.abs(nav.grooveHeadingErrorDeg) < 4 && Math.abs(nav.deck.y) < 450) ||
      nav.turnedDeg > 230,
    /** Split for the phase strip: the 90 is where 90 degrees of turn remain. */
    subPhase: (nav) => (Math.abs(nav.grooveHeadingErrorDeg) < 100 ? 'THE 90' : 'THE 180'),
  },

  {
    id: 'GROOVE',
    label: 'THE GROOVE',
    brief: 'Wings level, centreline, on the ball. Call it.',
    guidance: { mode: 'groove' },
    altFt: null,
    glideslope: true,
    speedKtas: null,
    flapNorm: 1,
    done: (nav) => nav.toTouchdownFt < 0,
  },

  {
    id: 'TOUCHDOWN',
    label: 'TOUCHDOWN',
    brief: 'Full power at the deck. You do not know you are stopping yet.',
    guidance: { mode: 'groove' },
    onDeck: true,
    // Zero, not "on speed". The wire is doing the flying now: 130 knots to a
    // standstill in about 300 feet, which is a little over 2 g and the reason
    // everything in the cockpit is strapped down.
    speedKtas: 0,
    flapNorm: 1,
    done: (nav) => nav.groundspeedKts < 55,
  },

  {
    id: 'ROLLOUT',
    label: 'ROLLOUT',
    brief: 'Stopped in the wires. Throttles back, clean it up.',
    guidance: { mode: 'groove' },
    onDeck: true,
    speedKtas: 0,
    flapNorm: 1,
    done: (nav) => nav.stoppedFor > 1.0,
  },

  {
    id: 'COMPLETE',
    label: 'IN THE WIRES',
    brief: 'Pass complete.',
    guidance: { mode: 'groove' },
    onDeck: true,
    speedKtas: 0,
    flapNorm: 1,
    done: () => false,
  },
]

export const LEG_BY_ID = Object.fromEntries(LEGS.map((l) => [l.id, l]))

/** Ordered ids, for the phase strip and for tests that walk the pattern. */
export const LEG_IDS = LEGS.map((l) => l.id)

/** Phase-strip labels. TOUCHDOWN and ROLLOUT collapse into one box. */
export const PHASE_STRIP = [
  { id: 'INITIAL', short: 'INITIAL' },
  { id: 'BREAK', short: 'BREAK' },
  { id: 'DOWNWIND', short: 'DOWNWIND' },
  { id: 'APPROACH_TURN', short: 'THE 180' },
  { id: 'GROOVE', short: 'GROOVE' },
  { id: 'TOUCHDOWN', short: 'TRAP' },
]
